import { Button, Flex, Spinner, Text } from '@chakra-ui/react'
import { useQueries } from '@tanstack/react-query'
import { useCallback, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router'
import { phoneNumberApi } from '~/apis/phoneNumberApi'
import { userApi } from '~/apis/userApi'
import { userGroupApi } from '~/apis/userGroupApi'
import { useUserState } from '~/atoms/UserState'
import { MangoModal } from '~/components/shared/MangoModal'
import { toaster } from '~/components/ui/toaster'
import { useCheckRole } from '~/hooks/useCheckRole'
import { useGetPbxList } from '~/queries/pbxQueries'
import { ApplicationRoutes } from '~/types/RoutingTypes'
import { getUserRoleClassification } from '~/utils/auth'

import { useListUserGroups, type UserGroup } from '@mangovoice/user-groups-ui'
import {
  EditPhonePermissionsDialog,
  extractFieldErrors,
  formatAssignedAssets,
  type PbxAssociation,
  type PbxUser,
  type PbxUserCreate,
  PbxUserFormDialog,
  type PbxUserFormDialogSubmitData,
  type PbxUserUpdate,
  RemoveUserError,
  useAddUserToPbx,
  useCreateUser,
  useGetUser,
  useGetUserAssignedRoles,
  useListUserPbxes,
  useListUserRoles,
  usePhoneNumberOptions,
  type UserCreate,
  UserDetailView,
  useRemoveUserFromPbx,
  useResetPasswordWithClipboard,
  UserForm,
  UserList,
  type UserUpdate,
  useUpdatePbxUser,
  useUpdateUser,
} from '@mangovoice/users'

import { UserDetailsPanel } from './UserDetailsPanel'

const isEveryoneAutoGeneratedGroup = (group: UserGroup): boolean =>
  group.auto_generated && group.name.trim().toLowerCase() === 'everyone'

export const UsersPage = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const { userId } = useParams<{ userId?: string }>()
  const { pbxId, userRoles } = useUserState({ canAutoRefresh: false })
  const checkRole = useCheckRole()
  const roleClassification = getUserRoleClassification(userRoles)
  const hasSeniorAccess = checkRole('senior')
  const canEdit = hasSeniorAccess
  const canDelete = hasSeniorAccess
  const canCreate = hasSeniorAccess

  const parsedPbxId = pbxId ?? NaN
  const parsedUserId = userId ? parseInt(userId, 10) : NaN
  const selectedUserId = Number.isFinite(parsedUserId)
    ? parsedUserId
    : undefined

  const {
    user,
    isLoading,
    refetch: refetchUser,
  } = useGetUser({
    api: userApi,
    userId: selectedUserId ?? 0,
    queryOptions: { enabled: selectedUserId != null },
  })
  const {
    userPbxes,
    isLoading: isUserPbxesLoading,
    refetch: refetchUserPbxes,
  } = useListUserPbxes({
    api: userApi,
    userId: selectedUserId ?? 0,
    queryOptions: { enabled: selectedUserId != null },
  })
  const {
    roles,
    isLoading: isRolesLoading,
    refetch: refetchRoles,
  } = useListUserRoles({
    api: userApi,
    queryOptions: { enabled: canEdit },
  })
  const {
    assignedRoles,
    isLoading: isAssignedRolesLoading,
    refetch: refetchAssignedRoles,
  } = useGetUserAssignedRoles({
    api: userApi,
    userId: selectedUserId ?? 0,
    queryOptions: { enabled: selectedUserId != null },
  })
  const { data: pbxListData } = useGetPbxList({
    apiParams: { limit: 1000 },
    options: { enabled: selectedUserId != null },
  })

  const userGroupQueries = useQueries({
    queries: userPbxes.map((pbx) => ({
      queryKey: ['webUsersPhonePermissions', selectedUserId ?? 0, pbx.id],
      queryFn: async (): Promise<UserGroup[]> => {
        const response = await userGroupApi.getUserGroups(pbx.id, {
          limit: 1000,
        })
        if (!response.ok) {
          throw new Error('Failed to fetch user groups')
        }
        return response.data?.results ?? []
      },
      enabled: (selectedUserId ?? 0) > 0 && pbx.id > 0,
      staleTime: 30 * 1000,
    })),
  })

  const userGroupsByPbxId = useMemo(() => {
    const groupsByPbx = new Map<number, UserGroup[]>()
    userPbxes.forEach((pbx, index) => {
      groupsByPbx.set(pbx.id, userGroupQueries[index]?.data ?? [])
    })
    return groupsByPbx
  }, [userGroupQueries, userPbxes])

  const phonePermissionPbxes = useMemo(
    () =>
      userPbxes.map((pbx) => ({
        pbxId: pbx.id,
        pbxName: pbx.name,
        groupNames: (userGroupsByPbxId.get(pbx.id) ?? [])
          .filter((group) => group.users.includes(selectedUserId ?? 0))
          .map((group) => group.name)
          .sort((a, b) => a.localeCompare(b)),
      })),
    [selectedUserId, userGroupsByPbxId, userPbxes],
  )

  const isPhonePermissionsLoading =
    isUserPbxesLoading ||
    (userPbxes.length > 0 &&
      userGroupQueries.some((query) => query.isLoading || query.isFetching))

  const unassociatedPbxes = useMemo(() => {
    const associatedPbxIds = new Set(userPbxes.map((pbx) => pbx.id))
    return (pbxListData?.results ?? [])
      .filter((pbx) => !associatedPbxIds.has(pbx.id))
      .map((pbx) => ({
        id: pbx.id,
        name: pbx.name || `PBX ${pbx.id}`,
      }))
  }, [pbxListData?.results, userPbxes])

  const pbxOptions = useMemo(
    () =>
      unassociatedPbxes.map((pbx) => ({
        id: pbx.id,
        name: pbx.name,
      })),
    [unassociatedPbxes],
  )

  const [isUserEditOpen, setIsUserEditOpen] = useState(false)
  const [isUserCreateOpen, setIsUserCreateOpen] = useState(false)
  const [createDialogPhoneNumbersPbxId, setCreateDialogPhoneNumbersPbxId] =
    useState<number | null>(Number.isFinite(parsedPbxId) ? parsedPbxId : null)
  const [pbxDialog, setPbxDialog] = useState<{
    isOpen: boolean
    mode: 'add' | 'edit'
    pbxId?: number
    pbxUser?: PbxUser | null
  }>({ isOpen: false, mode: 'add' })
  const [pbxDialogPhoneNumbersPbxId, setPbxDialogPhoneNumbersPbxId] = useState<
    number | null
  >(null)

  const {
    options: pbxDialogPhoneNumberOptions,
    isFetching: isPbxDialogPhoneNumbersFetching,
  } = usePhoneNumberOptions({
    pbxId: pbxDialogPhoneNumbersPbxId,
    getPhoneNumbers: phoneNumberApi.getPhoneNumbers,
    queryKeyScope: 'web-app-UsersPage',
  })

  // Load user groups for whichever PBX is currently active in the dialog so
  // the form can render the "User Groups" multi-select. The dialog fires
  // onPbxChange (setPbxDialogPhoneNumbersPbxId) in both add and edit modes.
  const { userGroups: pbxDialogUserGroups } = useListUserGroups({
    api: userGroupApi,
    pbxId: pbxDialogPhoneNumbersPbxId ?? 0,
    queryOptions: { enabled: pbxDialogPhoneNumbersPbxId != null },
  })
  const pbxDialogUserGroupOptions = useMemo(
    () =>
      pbxDialogUserGroups.map((group) => ({
        id: group.id,
        name: group.name,
        pbx_id: group.pbx_id,
      })),
    [pbxDialogUserGroups],
  )

  const {
    options: createDialogPhoneNumberOptions,
    isFetching: isCreateDialogPhoneNumbersFetching,
  } = usePhoneNumberOptions({
    pbxId: isUserCreateOpen ? createDialogPhoneNumbersPbxId : null,
    getPhoneNumbers: phoneNumberApi.getPhoneNumbers,
    queryKeyScope: 'web-app-UsersPage-create',
  })

  // Load user groups for the create-user dialog so the form can render the
  // "User Groups" multi-select. Keyed off the create dialog's active PBX.
  const { userGroups: createDialogUserGroups } = useListUserGroups({
    api: userGroupApi,
    pbxId: createDialogPhoneNumbersPbxId ?? 0,
    queryOptions: {
      enabled: isUserCreateOpen && createDialogPhoneNumbersPbxId != null,
    },
  })
  const createDialogUserGroupOptions = useMemo(
    () =>
      createDialogUserGroups.map((group) => ({
        id: group.id,
        name: group.name,
        pbx_id: group.pbx_id,
      })),
    [createDialogUserGroups],
  )
  const [removeDialog, setRemoveDialog] = useState<{
    isOpen: boolean
    pbxId: number
    userId: number
    userName: string
    assignedExtensionCount: number
    assignedDeviceCount: number
  }>({
    isOpen: false,
    pbxId: 0,
    userId: 0,
    userName: '',
    assignedExtensionCount: 0,
    assignedDeviceCount: 0,
  })
  const [phonePermissionsDialog, setPhonePermissionsDialog] = useState<{
    isOpen: boolean
    pbxId: number
  }>({ isOpen: false, pbxId: 0 })
  const [isPhonePermissionsSubmitting, setIsPhonePermissionsSubmitting] =
    useState(false)

  const updateUserMutation = useUpdateUser({ api: userApi })
  const createUserMutation = useCreateUser({ api: userApi })
  const addToPbxMutation = useAddUserToPbx({ api: userApi })
  const updatePbxUserMutation = useUpdatePbxUser({ api: userApi })
  const removePbxUserMutation = useRemoveUserFromPbx({ api: userApi })
  const [createServerErrors, setCreateServerErrors] = useState<
    Record<string, string> | undefined
  >(undefined)
  const [editServerErrors, setEditServerErrors] = useState<
    Record<string, string> | undefined
  >(undefined)
  const { resetPassword, isResetPasswordLoading } =
    useResetPasswordWithClipboard({
      api: userApi,
      toast: toaster,
      messages: {
        copiedTitle: t(
          'users.resetPassword.toast.copiedTitle',
          'Reset link copied to clipboard',
        ),
        copyFailedTitle: t(
          'users.resetPassword.toast.copyFailedTitle',
          'Reset link generated but could not be copied',
        ),
        manualCopyDescription: (url: string) =>
          t(
            'users.resetPassword.toast.manualCopyDescription',
            'Copy this reset URL manually: {{url}}',
            { url },
          ),
        missingUrlTitle: t(
          'users.resetPassword.toast.missingUrlTitle',
          'Failed to generate reset link',
        ),
        missingUrlDescription: t(
          'users.resetPassword.toast.missingUrlDescription',
          'No reset URL was returned.',
        ),
        requestFailedTitle: t(
          'users.resetPassword.toast.requestFailedTitle',
          'Failed to reset password',
        ),
        unexpectedErrorDescription: t(
          'users.resetPassword.toast.unexpectedErrorDescription',
          'An unexpected error occurred.',
        ),
      },
    })

  const getUserDetailHref = useCallback(
    (userId: number) => {
      const queryString = searchParams.toString()
      const basePath = ApplicationRoutes.USER_DETAIL.replace(
        ':userId',
        String(userId),
      )
      return queryString ? `${basePath}?${queryString}` : basePath
    },
    [searchParams],
  )

  const handleNavigateToUser = useCallback(
    (userId: number) => {
      navigate(getUserDetailHref(userId))
    },
    [getUserDetailHref, navigate],
  )

  const handleClosed = () => {
    const queryString = searchParams.toString()
    navigate(
      queryString
        ? `${ApplicationRoutes.USERS}?${queryString}`
        : ApplicationRoutes.USERS,
    )
  }

  const handleEdit = useCallback(() => {
    setIsUserEditOpen(true)
  }, [])

  const handleOpenCreateUser = useCallback(() => {
    setCreateDialogPhoneNumbersPbxId(
      Number.isFinite(parsedPbxId) ? parsedPbxId : null,
    )
    setIsUserCreateOpen(true)
  }, [parsedPbxId])

  const handleCloseCreateUser = useCallback(() => {
    setIsUserCreateOpen(false)
    setCreateServerErrors(undefined)
  }, [])

  const handleSubmitCreateUser = useCallback(
    (data: UserCreate | UserUpdate) => {
      setCreateServerErrors(undefined)
      const createData = data as UserCreate
      createUserMutation.mutate(createData, {
        onSuccess: (created) => {
          toaster.create({
            title: t('users.toast.userCreated.title', 'User created'),
            description: t(
              'users.toast.userCreated.description',
              '{{firstName}} {{lastName}} has been created.',
              {
                firstName: createData.first_name,
                lastName: createData.last_name,
              },
            ),
            type: 'success',
            duration: 4000,
          })
          setIsUserCreateOpen(false)
          if (created?.id) {
            navigate(getUserDetailHref(created.id))
          }
        },
        onError: (err) => {
          const fieldErrors = extractFieldErrors(err.rawErrorData)
          if (Object.keys(fieldErrors).length > 0) {
            setCreateServerErrors(fieldErrors)
          }
          toaster.create({
            title: t(
              'users.toast.userCreateError.title',
              'Error creating user',
            ),
            description: err.message,
            type: 'error',
            duration: 5000,
          })
        },
      })
    },
    [createUserMutation, getUserDetailHref, navigate, t],
  )

  const handleResetPassword = useCallback(() => {
    if (!selectedUserId) return
    resetPassword(selectedUserId)
  }, [resetPassword, selectedUserId])

  const handleCloseEditDialog = useCallback(() => {
    setIsUserEditOpen(false)
    setEditServerErrors(undefined)
  }, [])

  const handleSubmitEditUser = useCallback(
    (data: UserCreate | UserUpdate) => {
      if (!selectedUserId) return
      setEditServerErrors(undefined)

      updateUserMutation.mutate(
        { userId: selectedUserId, data: data as UserUpdate },
        {
          onSuccess: async () => {
            toaster.create({
              title: t('users.toast.userUpdated.title', 'User updated'),
              type: 'success',
              duration: 4000,
            })
            setIsUserEditOpen(false)
            await refetchUser()
          },
          onError: (err) => {
            const fieldErrors = extractFieldErrors(err.rawErrorData)
            if (Object.keys(fieldErrors).length > 0) {
              setEditServerErrors(fieldErrors)
            }
            toaster.create({
              title: t(
                'users.toast.userUpdateError.title',
                'Error updating user',
              ),
              description: err.message,
              type: 'error',
              duration: 5000,
            })
          },
        },
      )
    },
    [refetchUser, selectedUserId, t, updateUserMutation],
  )

  const handleAddToPbx = useCallback(() => {
    setPbxDialog({ isOpen: true, mode: 'add' })
  }, [])

  const handleEditPbxAssociation = useCallback(
    (association: PbxAssociation) => {
      setPbxDialog({
        isOpen: true,
        mode: 'edit',
        pbxId: association.pbxId,
        pbxUser: association.pbxUser,
      })
    },
    [],
  )

  const handlePbxDialogClose = useCallback(() => {
    setPbxDialog({ isOpen: false, mode: 'add' })
  }, [])

  const handlePbxDialogSubmit = useCallback(
    ({ pbxId, data }: PbxUserFormDialogSubmitData) => {
      if (!selectedUserId) return

      if (pbxDialog.mode === 'add') {
        addToPbxMutation.mutate(
          { pbxId, data: data as PbxUserCreate },
          {
            onSuccess: async () => {
              toaster.create({
                title: t('users.toast.pbxAdded.title', 'User added to PBX'),
                type: 'success',
                duration: 4000,
              })
              await Promise.all([refetchUser(), refetchUserPbxes()])
              handlePbxDialogClose()
            },
            onError: (err) => {
              toaster.create({
                title: t('users.toast.error', 'Error'),
                description: err.message,
                type: 'error',
                duration: 5000,
              })
            },
          },
        )
      } else {
        updatePbxUserMutation.mutate(
          {
            pbxId,
            userId: selectedUserId,
            data: data as PbxUserUpdate,
          },
          {
            onSuccess: async () => {
              toaster.create({
                title: t(
                  'users.toast.pbxUpdated.title',
                  'PBX association updated',
                ),
                type: 'success',
                duration: 4000,
              })
              await Promise.all([refetchUser(), refetchUserPbxes()])
              handlePbxDialogClose()
            },
            onError: (err) => {
              toaster.create({
                title: t('users.toast.error', 'Error'),
                description: err.message,
                type: 'error',
                duration: 5000,
              })
            },
          },
        )
      }
    },
    [
      addToPbxMutation,
      handlePbxDialogClose,
      pbxDialog.mode,
      refetchUser,
      refetchUserPbxes,
      selectedUserId,
      t,
      updatePbxUserMutation,
    ],
  )

  const handleRemovePbxUser = useCallback(
    (
      pbxId: number,
      userId: number,
      userName: string,
      assignedExtensionCount: number,
      assignedDeviceCount: number,
    ) => {
      setRemoveDialog({
        isOpen: true,
        pbxId,
        userId,
        userName,
        assignedExtensionCount,
        assignedDeviceCount,
      })
    },
    [],
  )

  const handleCloseRemoveDialog = useCallback(() => {
    setRemoveDialog({
      isOpen: false,
      pbxId: 0,
      userId: 0,
      userName: '',
      assignedExtensionCount: 0,
      assignedDeviceCount: 0,
    })
  }, [])

  const handleConfirmRemovePbxUser = useCallback(() => {
    removePbxUserMutation.mutate(
      { pbxId: removeDialog.pbxId, userId: removeDialog.userId },
      {
        onSuccess: async () => {
          toaster.create({
            title: t(
              'users.toast.associationRemoved.title',
              'Association removed',
            ),
            description: t(
              'users.toast.associationRemoved.description',
              '{{name}} has been removed from this PBX.',
              {
                name:
                  removeDialog.userName ||
                  t('users.toast.defaultUserName', 'User'),
              },
            ),
            type: 'success',
            duration: 4000,
          })
          handleCloseRemoveDialog()
          await Promise.all([refetchUser(), refetchUserPbxes()])
        },
        onError: (err) => {
          if (err instanceof RemoveUserError && err.code === 'not_found') {
            toaster.create({
              title: t('users.toast.alreadyRemoved.title', 'Already removed'),
              description: t(
                'users.toast.alreadyRemoved.description',
                'This user was not found in the PBX and may have already been removed.',
              ),
              type: 'info',
              duration: 4000,
            })
            handleCloseRemoveDialog()
            return
          }

          toaster.create({
            title: t('users.toast.error', 'Error'),
            description:
              err.message ||
              t('users.toast.removeFailed', 'Failed to remove user from PBX.'),
            type: 'error',
            duration: 5000,
          })
        },
      },
    )
  }, [
    handleCloseRemoveDialog,
    refetchUser,
    refetchUserPbxes,
    removeDialog.pbxId,
    removeDialog.userId,
    removeDialog.userName,
    removePbxUserMutation,
    t,
  ])

  const handleOpenPhonePermissionsDialog = useCallback((pbxId: number) => {
    setPhonePermissionsDialog({ isOpen: true, pbxId })
  }, [])

  const handleViewAssignedExtensions = useCallback(
    (_targetPbxId: number) => {
      if (selectedUserId == null) return
      // Gating for cross-PBX navigation happens via `canViewAssignedExtensions`
      // so non-matching cards render as plain text and never reach this handler.
      navigate(
        `${ApplicationRoutes.EXTENSIONS}?view=lines&assignedUserFilter=${encodeURIComponent(selectedUserId)}`,
      )
    },
    [navigate, selectedUserId],
  )

  const canViewAssignedExtensions = useCallback(
    (targetPbxId: number) => targetPbxId === pbxId,
    [pbxId],
  )

  const handleClosePhonePermissionsDialog = useCallback(() => {
    setPhonePermissionsDialog({ isOpen: false, pbxId: 0 })
  }, [])

  const selectedPhonePermissionsPbx = useMemo(
    () =>
      userPbxes.find((pbx) => pbx.id === phonePermissionsDialog.pbxId) ?? null,
    [phonePermissionsDialog.pbxId, userPbxes],
  )

  const selectedPhonePermissionsGroups = useMemo(
    () =>
      selectedPhonePermissionsPbx
        ? (userGroupsByPbxId.get(selectedPhonePermissionsPbx.id) ?? [])
        : [],
    [selectedPhonePermissionsPbx, userGroupsByPbxId],
  )

  const phonePermissionGroupOptions = useMemo(
    () =>
      selectedPhonePermissionsGroups.map((group) => ({
        id: group.id,
        name: group.name,
        locked: isEveryoneAutoGeneratedGroup(group),
      })),
    [selectedPhonePermissionsGroups],
  )

  const initiallySelectedPhonePermissionGroupIds = useMemo(
    () =>
      selectedPhonePermissionsGroups
        .filter((group) => group.users.includes(selectedUserId ?? 0))
        .map((group) => group.id),
    [selectedPhonePermissionsGroups, selectedUserId],
  )

  const userDisplayName = useMemo(() => {
    if (!user) return t('users.toast.defaultUserName', 'User')
    return (
      user.name ||
      `${user.first_name} ${user.last_name}`.trim() ||
      user.username
    )
  }, [t, user])

  const handlePhonePermissionsSubmit = useCallback(
    async (selectedGroupIds: number[]) => {
      const pbxId = phonePermissionsDialog.pbxId
      if (!pbxId || !selectedUserId) return

      const groups = userGroupsByPbxId.get(pbxId) ?? []
      const selectedSet = new Set(selectedGroupIds)

      const updates = groups
        .map((group) => {
          const shouldHaveUser =
            isEveryoneAutoGeneratedGroup(group) || selectedSet.has(group.id)
          const hasUser = group.users.includes(selectedUserId)
          if (shouldHaveUser === hasUser) return null

          return {
            id: group.id,
            name: group.name,
            users: shouldHaveUser
              ? Array.from(new Set([...group.users, selectedUserId]))
              : group.users.filter((id) => id !== selectedUserId),
          }
        })
        .filter(
          (
            update,
          ): update is {
            id: number
            name: string
            users: number[]
          } => update != null,
        )

      if (updates.length === 0) {
        toaster.create({
          title: t(
            'users.toast.noPhonePermissionChanges.title',
            'No phone permission changes',
          ),
          type: 'info',
          duration: 3000,
        })
        handleClosePhonePermissionsDialog()
        return
      }

      try {
        setIsPhonePermissionsSubmitting(true)

        for (const update of updates) {
          const response = await userGroupApi.updateUserGroup(
            pbxId,
            update.id,
            {
              users: update.users,
            },
          )

          if (!response.ok) {
            throw new Error(
              t(
                'users.toast.updateGroupFailed',
                'Failed to update "{{name}}".',
                { name: update.name },
              ),
            )
          }
        }

        toaster.create({
          title: t(
            'users.toast.phonePermissionsUpdated.title',
            'Phone permissions updated',
          ),
          type: 'success',
          duration: 4000,
        })
        handleClosePhonePermissionsDialog()
        await Promise.all([
          refetchUser(),
          refetchUserPbxes(),
          ...userGroupQueries.map((query) => query.refetch()),
        ])
      } catch (err) {
        toaster.create({
          title: t(
            'users.toast.phonePermissionsUpdateError.title',
            'Failed to update phone permissions',
          ),
          description:
            err instanceof Error
              ? err.message
              : t(
                  'users.toast.phonePermissionsUpdateError.fallback',
                  'Unable to update phone permissions.',
                ),
          type: 'error',
          duration: 5000,
        })
      } finally {
        setIsPhonePermissionsSubmitting(false)
      }
    },
    [
      handleClosePhonePermissionsDialog,
      phonePermissionsDialog.pbxId,
      refetchUser,
      refetchUserPbxes,
      selectedUserId,
      t,
      userGroupQueries,
      userGroupsByPbxId,
    ],
  )

  if (!hasSeniorAccess) {
    return <Navigate to={`${ApplicationRoutes.SETTINGS}/profile`} replace />
  }

  return (
    <Flex
      flexDirection="row"
      width="100%"
      height="full"
      minHeight={0}
      alignSelf="stretch"
      position="relative"
      overflow="hidden"
      gap={0}
      p={4}
    >
      <Flex
        flex="1"
        minWidth={0}
        minHeight={0}
        overflowY="auto"
        alignItems="flex-start"
        width={'100%'}
        height={'full'}
      >
        <UserList
          api={userApi}
          pbxId={parsedPbxId}
          onNavigateToUser={handleNavigateToUser}
          getUserDetailHref={getUserDetailHref}
          onNavigateToNew={handleOpenCreateUser}
          canCreate={canCreate}
          hideCreateButton={!canCreate}
          canDelete={canDelete}
          toast={toaster}
          t={t}
          paginationI18nPrefix="users.list.pagination"
        />
      </Flex>
      <UserDetailsPanel userId={selectedUserId} onClosed={handleClosed}>
        <Flex width="100%" p="0 1.25rem">
          <UserDetailView
            api={userApi}
            user={user ?? null}
            pbxAssociations={undefined}
            phonePermissions={{
              pbxPermissions: phonePermissionPbxes,
              isLoading: isPhonePermissionsLoading,
              canEdit,
              onEditPbx: handleOpenPhonePermissionsDialog,
            }}
            isLoading={isLoading || isUserPbxesLoading}
            onEdit={handleEdit}
            onAddToPbx={handleAddToPbx}
            onEditPbxAssociation={handleEditPbxAssociation}
            onViewAssignedExtensions={handleViewAssignedExtensions}
            canViewAssignedExtensions={canViewAssignedExtensions}
            onRemovePbxUser={handleRemovePbxUser}
            canEdit={canEdit}
            canDelete={canDelete}
            showAddToPbxButton={canEdit}
            onResetPassword={hasSeniorAccess ? handleResetPassword : undefined}
            isResetPasswordLoading={isResetPasswordLoading}
            t={t}
          />
        </Flex>
      </UserDetailsPanel>

      <MangoModal
        open={isUserEditOpen}
        onOpenChange={({ open }) => {
          if (!open) handleCloseEditDialog()
        }}
        maxWidth="900px"
        ariaLabel={t('users.form.heading.editUser', 'Edit User')}
      >
        {isLoading || isRolesLoading || isAssignedRolesLoading || !user ? (
          <Flex py={8} justify="center" align="center" gap={3}>
            <Spinner size="sm" />
            <Text fontSize="sm" color="fg.muted">
              {t('users.edit.loadingDetails', 'Loading user details...')}
            </Text>
          </Flex>
        ) : (
          <>
            <UserForm
              api={userApi}
              mode="edit"
              user={user}
              roles={roles}
              assignedRoles={assignedRoles}
              isSubmitting={updateUserMutation.isPending}
              onSubmit={handleSubmitEditUser}
              onCancel={handleCloseEditDialog}
              isStaff={roleClassification.isMangoStaff}
              isSuperuser={roleClassification.isSuperuser}
              canAssignRole={canEdit}
              t={t}
              serverErrors={editServerErrors}
            />
            {roles.length === 0 && roleClassification.isMangoStaff && (
              <Flex justify="flex-end" mt={3}>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    void refetchRoles()
                    void refetchAssignedRoles()
                  }}
                >
                  {t('users.edit.retryLoadingRoles', 'Retry loading roles')}
                </Button>
              </Flex>
            )}
          </>
        )}
      </MangoModal>

      <MangoModal
        open={isUserCreateOpen}
        onOpenChange={({ open }) => {
          if (!open) handleCloseCreateUser()
        }}
        maxWidth="900px"
        ariaLabel={t('users.form.heading.newUser', 'New User')}
      >
        {isRolesLoading ? (
          <Flex py={8} justify="center" align="center" gap={3}>
            <Spinner size="sm" />
            <Text fontSize="sm" color="fg.muted">
              {t('users.create.loadingForm', 'Loading user form...')}
            </Text>
          </Flex>
        ) : (
          <UserForm
            api={userApi}
            mode="create"
            roles={roles}
            phoneNumberOptions={createDialogPhoneNumberOptions}
            isPhoneNumbersLoading={isCreateDialogPhoneNumbersFetching}
            userGroupOptions={createDialogUserGroupOptions}
            onPbxChange={setCreateDialogPhoneNumbersPbxId}
            defaultPbxId={
              Number.isFinite(parsedPbxId) ? parsedPbxId : undefined
            }
            isSubmitting={createUserMutation.isPending}
            onSubmit={handleSubmitCreateUser}
            onCancel={handleCloseCreateUser}
            isStaff={roleClassification.isMangoStaff}
            isSuperuser={roleClassification.isSuperuser}
            canAssignRole={canEdit}
            t={t}
            serverErrors={createServerErrors}
          />
        )}
      </MangoModal>

      <PbxUserFormDialog
        isOpen={pbxDialog.isOpen}
        onClose={handlePbxDialogClose}
        mode={pbxDialog.mode}
        userId={selectedUserId ?? 0}
        pbxUser={pbxDialog.pbxUser}
        pbxId={pbxDialog.pbxId}
        pbxOptions={pbxOptions}
        userGroupOptions={pbxDialogUserGroupOptions}
        phoneNumberOptions={pbxDialogPhoneNumberOptions}
        isPhoneNumbersLoading={isPbxDialogPhoneNumbersFetching}
        onPbxChange={setPbxDialogPhoneNumbersPbxId}
        isSubmitting={
          addToPbxMutation.isPending || updatePbxUserMutation.isPending
        }
        onSubmit={handlePbxDialogSubmit}
        t={t}
      />

      <EditPhonePermissionsDialog
        isOpen={phonePermissionsDialog.isOpen}
        onClose={handleClosePhonePermissionsDialog}
        userName={userDisplayName}
        pbxName={selectedPhonePermissionsPbx?.name ?? ''}
        groups={phonePermissionGroupOptions}
        initialSelectedGroupIds={initiallySelectedPhonePermissionGroupIds}
        isSubmitting={isPhonePermissionsSubmitting}
        onSubmit={(selectedGroupIds) => {
          void handlePhonePermissionsSubmit(selectedGroupIds)
        }}
        t={t}
      />

      <MangoModal
        open={
          removeDialog.isOpen &&
          (removeDialog.assignedExtensionCount > 0 ||
            removeDialog.assignedDeviceCount > 0)
        }
        onOpenChange={({ open }) => {
          if (!open) handleCloseRemoveDialog()
        }}
        header={t(
          'users.removeDialog.cannotTitle',
          'Cannot remove user from PBX',
        )}
        footer={
          <Button colorPalette="brand" onClick={handleCloseRemoveDialog}>
            {t('users.removeDialog.ok', 'OK')}
          </Button>
        }
      >
        <Text fontSize="sm" color="fg.muted">
          <Trans
            i18nKey="users.removeDialog.cannotBody"
            defaults="<bold>{{userName}}</bold> has {{assets}} on this PBX. Re-assign or delete these assets before removing this user from the PBX."
            values={{
              userName:
                removeDialog.userName ||
                t('users.removeDialog.thisUserCapitalized', 'This user'),
              assets: formatAssignedAssets(
                removeDialog.assignedExtensionCount,
                removeDialog.assignedDeviceCount,
                t,
              ),
            }}
            components={{
              bold: <Text as="span" fontWeight="semibold" color="fg.default" />,
            }}
          />
        </Text>
      </MangoModal>

      <MangoModal
        open={
          removeDialog.isOpen &&
          removeDialog.assignedExtensionCount === 0 &&
          removeDialog.assignedDeviceCount === 0
        }
        onOpenChange={({ open }) => {
          if (!open) handleCloseRemoveDialog()
        }}
        header={t('users.removeDialog.confirmTitle', 'Remove user from PBX?')}
        footer={
          <Flex gap={2}>
            <Button
              variant="outline"
              colorPalette="neutral"
              onClick={handleCloseRemoveDialog}
            >
              {t('users.removeDialog.cancel', 'Cancel')}
            </Button>
            <Button
              colorPalette="error"
              onClick={handleConfirmRemovePbxUser}
              loading={removePbxUserMutation.isPending}
            >
              {t('users.removeDialog.remove', 'Remove')}
            </Button>
          </Flex>
        }
      >
        <Text fontSize="sm" color="fg.muted">
          <Trans
            i18nKey="users.removeDialog.confirmBody"
            defaults="This will remove <bold>{{userName}}</bold> from the selected PBX."
            values={{
              userName:
                removeDialog.userName ||
                t('users.removeDialog.thisUser', 'this user'),
            }}
            components={{
              bold: <Text as="span" fontWeight="semibold" color="fg.default" />,
            }}
          />
        </Text>
      </MangoModal>
    </Flex>
  )
}
